import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { usePayments } from '../hooks/usePayments';
import TransactionList from '../components/TransactionList';
import BottomNavigation from '@/components/ui/BottomNavigation';
import { toPersianNumber } from '@/utils/persianNumber';

interface PaymentHistoryProps {
  className?: string;
}

const PaymentHistory = ({ className }: PaymentHistoryProps) => {
  const {
    transactions,
    error,
    sumSuccess,
    totalCount,
  } = usePayments();

  const navigate = useNavigate();

  // Use backend-provided sum of successful payments for accuracy
  const totalAmount = sumSuccess;

  // Calculate total number of completed transactions
  const completedTransactions = transactions.filter(t => t.status === 'completed').length;

  const handleGoBack = () => {
    navigate(-1); // Go back to the previous page
  };

  // Remove loading state entirely
  if (error) {
    return (
      <div className={cn("min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center", className)}>
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pb-20", className)}>
      {/* Header */}
      <div className="bg-[#ffffff] px-4 py-3 flex items-center justify-between sticky top-0 z-50 pt-safe-top">
        <div className="flex items-center justify-between w-full mt-2">
        <button
          onClick={handleGoBack}
          className="p-1.5 hover:bg-gray-100 rounded-full transition-all duration-200 hover:scale-110"
          aria-label="بازگشت"
        >
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"></path>
          </svg>
        </button>
        <h1 className="text-sm md:text-base font-bold text-foreground">تاریخچه پرداخت‌ها</h1>
        <div className="w-8"></div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="container mx-auto p-4 pt-6 pb-6"
      >

        {/* Summary Boxes */}
        <div className="flex flex-row gap-4 mb-8">
          <div className="bg-white rounded-card p-4 border border-soft-neutral-200 flex-1 text-center">
            <h3 className="text-xs md:text-sm font-bold text-foreground mb-1 font-persian">
              مقدار پرداخت شده کل
            </h3>
            <p className="text-base md:text-lg font-bold text-primary-600 font-persian">
              {new Intl.NumberFormat('fa-IR').format(totalAmount)} تومان
            </p>
            <p className="text-muted text-[10px] md:text-xs mt-1 font-persian">
              در مجموع
            </p>
          </div>
          <div className="bg-white rounded-card p-4 border border-soft-neutral-200 flex-1 text-center">
            <h3 className="text-xs md:text-sm font-bold text-foreground mb-1 font-persian">
              تعداد تراکنش‌های موفق
            </h3>
            <p className="text-lg md:text-xl font-bold text-primary-600 font-persian">
              {toPersianNumber(completedTransactions)}
            </p>
            <p className="text-muted text-[10px] md:text-xs mt-1 font-persian">
              تراکنش
            </p>
          </div>
        </div>

        {/* Transaction List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <TransactionList
            transactions={transactions}
          />
        </div>
      </motion.div>

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
};

export default PaymentHistory;
